/**
 * Before/After Comparison Slider snippet-author runtime.
 *
 * Binds each `[data-spectrum-instance-id][data-spectrum-snippet-id="ttt35jjm"]`
 * container on the page. Two responsibilities per instance:
 *
 *   1. Interaction — a draggable vertical divider (pointer + keyboard) that
 *      sets `--sai-pos` (a percentage) on the frame. The "before" layer is
 *      clipped to that position in CSS, revealing the "after" layer to its right.
 *   2. Variant binding — `__spectrumAi.snippet.bind` fires once with the
 *      SSR-resolved default variant, then again on every `$spectrum:variant_resolved`
 *      event; `applyVariant` rewrites both layers' images (desktop <img> + mobile
 *      <source>), copy-band heading + subheading, corner-badge text/visibility,
 *      handle color, aspect ratios, and the start position. The layout preset is
 *      SSR identity only — it is not re-applied on variant swap.
 *
 * Container-scoped: every DOM read/write goes through `node`; multi-render pages
 * cannot collide. No global side effects on load.
 *
 * SRCSET_WIDTHS — keep in sync with the srcset block in
 * _sai-snippet-ttt35jjm.liquid. The drift test in __tests__/srcset-parity.test.ts
 * asserts equality.
 *
 * Test surface: when `globalThis.__SAI_TEST_HARNESS__ === true`, the IIFE exposes
 * `globalThis.__saiTtt35jjm` with the pure helpers + `wireInstance`/`applyVariant`
 * for unit tests. Production never sets the flag, so production never carries the
 * global — keeps "no global side effects on load" honest.
 */
;(() => {
  const SNIPPET_ID = 'ttt35jjm'
  const ROOT_SELECTOR = `.sai-${SNIPPET_ID}`
  const FRAME_SELECTOR = `.sai-${SNIPPET_ID}__frame`
  const HANDLE_SELECTOR = `.sai-${SNIPPET_ID}__handle`
  const SRCSET_WIDTHS = [600, 1200, 2400]
  const PRIMARY_WIDTH = 2400
  const KEYBOARD_STEP = 2
  const EVENT_NAMESPACE = 'before_after_slider'

  // ── pure helpers ─────────────────────────────────────────────────────────

  function clamp(n, lo, hi) {
    if (n < lo) return lo
    if (n > hi) return hi
    return n
  }

  function appendWidth(url, w) {
    try {
      const u = new URL(url, window.location.href)
      u.searchParams.set('width', String(w))
      return u.toString()
    } catch {
      const sep = url.includes('?') ? '&' : '?'
      return `${url}${sep}width=${w}`
    }
  }

  function buildSrcset(baseUrl) {
    return SRCSET_WIDTHS.map((w) => `${appendWidth(baseUrl, w)} ${w}w`).join(', ')
  }

  function resolveImage(marker, pools) {
    if (!marker || marker.kind !== 'images' || !marker.refId) return null
    const resolved = pools?.images ? pools.images[marker.refId] : null
    return resolved && typeof resolved.url === 'string' ? resolved : null
  }

  // ── per-layer image update (desktop <img> + optional mobile <source>) ──────

  function applyLayer(node, layer, desktopMarker, mobileMarker, pools) {
    const picture = node.querySelector(`.sai-${SNIPPET_ID}__img--${layer}`)
    if (!picture) return
    const img = picture.querySelector('img')
    if (!img) return

    const desktop = resolveImage(desktopMarker, pools)
    if (desktop) {
      img.src = appendWidth(desktop.url, PRIMARY_WIDTH)
      img.srcset = buildSrcset(desktop.url)
      if (typeof desktop.width === 'number') img.width = desktop.width
      if (typeof desktop.height === 'number') img.height = desktop.height
      img.alt = typeof desktop.alt === 'string' ? desktop.alt : ''
    }

    const mobile = resolveImage(mobileMarker, pools)
    let source = picture.querySelector('source')
    if (mobile) {
      if (!source) {
        source = document.createElement('source')
        source.setAttribute('media', '(max-width: 767px)')
        source.setAttribute('sizes', '100vw')
        picture.insertBefore(source, img)
      }
      source.setAttribute('srcset', buildSrcset(mobile.url))
    } else if (source) {
      source.remove()
    }
  }

  // ── text + visibility helpers ──────────────────────────────────────────────

  function applyText(node, selector, value, visible) {
    const el = node.querySelector(selector)
    if (!el) return
    const text = typeof value === 'string' ? value : ''
    el.textContent = text
    el.hidden = !visible || text === ''
  }

  // ── full variant application ───────────────────────────────────────────────

  function applyVariant(node, content, pools) {
    const root = node.querySelector(ROOT_SELECTOR)
    const frame = node.querySelector(FRAME_SELECTOR)
    if (!root || !frame) return

    applyLayer(node, 'before', content.before_image, content.before_image_mobile, pools)
    applyLayer(node, 'after', content.after_image, content.after_image_mobile, pools)

    const showHeading = content.show_heading !== false
    const showBadges = content.show_badges !== false

    applyText(node, `.sai-${SNIPPET_ID}__heading`, content.heading, showHeading)
    applyText(node, `.sai-${SNIPPET_ID}__subheading`, content.subheading, showHeading)
    applyText(node, `.sai-${SNIPPET_ID}__badge--before`, content.before_badge, showBadges)
    applyText(node, `.sai-${SNIPPET_ID}__badge--after`, content.after_badge, showBadges)

    if (typeof content.handle_color === 'string' && content.handle_color !== '') {
      root.style.setProperty('--sai-handle-color', content.handle_color)
    }

    if (typeof content.aspect_ratio === 'string' && content.aspect_ratio !== '') {
      root.style.setProperty('--sai-ar-d', content.aspect_ratio)
    }
    if (
      typeof content.aspect_ratio_mobile === 'string' &&
      content.aspect_ratio_mobile !== 'inherit'
    ) {
      root.style.setProperty('--sai-ar-m', content.aspect_ratio_mobile)
    } else {
      root.style.removeProperty('--sai-ar-m')
    }

    if (content.start_position != null && content.start_position !== '') {
      const pos = clamp(Number(content.start_position), 0, 100)
      if (Number.isFinite(pos)) setPosition(frame, pos)
    }
  }

  // ── divider position ───────────────────────────────────────────────────────

  function setPosition(frame, pct) {
    const clamped = clamp(pct, 0, 100)
    frame.style.setProperty('--sai-pos', `${clamped}%`)
    const handle = frame.querySelector(HANDLE_SELECTOR)
    if (handle) handle.setAttribute('aria-valuenow', String(Math.round(clamped)))
    return clamped
  }

  function readPosition(frame) {
    const handle = frame.querySelector(HANDLE_SELECTOR)
    const now = handle ? Number(handle.getAttribute('aria-valuenow')) : Number.NaN
    return Number.isFinite(now) ? now : 50
  }

  // ── interaction wiring (once per container) ────────────────────────────────

  function wireInstance(node) {
    // `node` is the wrapper container in production (the root is a descendant)
    // but may be the root element itself in tests — resolve both.
    const root = node.matches?.(ROOT_SELECTOR) ? node : node.querySelector(ROOT_SELECTOR)
    const frame = node.querySelector(FRAME_SELECTOR)
    const handle = node.querySelector(HANDLE_SELECTOR)
    if (!frame || !handle) return null

    let dragging = false
    let dragged = false
    let trackFn = null

    function pctFromClientX(clientX) {
      const rect = frame.getBoundingClientRect()
      if (!rect.width) return readPosition(frame)
      return ((clientX - rect.left) / rect.width) * 100
    }

    function onFirstDrag() {
      if (dragged) return
      dragged = true
      if (typeof trackFn === 'function') {
        trackFn(`${EVENT_NAMESPACE}:slider_dragged`, {
          position_percent: Math.round(readPosition(frame)),
        })
      }
    }

    function onMove(e) {
      if (!dragging) return
      setPosition(frame, pctFromClientX(e.clientX))
    }

    function onUp() {
      dragging = false
      if (root) root.classList.remove('is-dragging')
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }

    function onDown(e) {
      // Mouse: primary button only. Touch/pen report button 0.
      if (e.button != null && e.button !== 0) return
      dragging = true
      if (root) root.classList.add('is-dragging')
      // Move first, then fire the event — so position_percent reflects where the
      // visitor actually interacted, not the stale pre-click divider position.
      setPosition(frame, pctFromClientX(e.clientX))
      onFirstDrag()
      if (typeof frame.setPointerCapture === 'function' && e.pointerId != null) {
        try {
          frame.setPointerCapture(e.pointerId)
        } catch {
          // Capture is a best-effort enhancement; window listeners cover the rest.
        }
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    }

    function onKey(e) {
      let next = null
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':
          next = readPosition(frame) - KEYBOARD_STEP
          break
        case 'ArrowRight':
        case 'ArrowUp':
          next = readPosition(frame) + KEYBOARD_STEP
          break
        case 'Home':
          next = 0
          break
        case 'End':
          next = 100
          break
        default:
          return
      }
      e.preventDefault()
      setPosition(frame, next)
      onFirstDrag()
    }

    frame.addEventListener('pointerdown', onDown)
    handle.addEventListener('keydown', onKey)

    return {
      setTrack(fn) {
        trackFn = fn
      },
    }
  }

  // ── test surface (gated) ────────────────────────────────────────────────────

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiTtt35jjm = {
      applyVariant,
      wireInstance,
      setPosition,
      readPosition,
      buildSrcset,
      appendWidth,
      clamp,
      SRCSET_WIDTHS,
      PRIMARY_WIDTH,
    }
  }

  // ── install ──────────────────────────────────────────────────────────────

  // Drag/keyboard interaction is a property of the snippet root, independent of
  // the wrapper that hosts it. Wire every root directly so the slider works
  // regardless of wrapper shape — the production per-instance wrapper
  // (data-spectrum-instance-id) and the dev-loop wrapper (data-spectrum-lq-snippet)
  // expose different container attributes, but both contain a `.sai-ttt35jjm` root.
  const instancesByRoot = new Map()
  for (const root of document.querySelectorAll(ROOT_SELECTOR)) {
    const instance = wireInstance(root)
    if (instance) instancesByRoot.set(root, instance)
  }

  // Variant binding is a production concern: the SDK swaps experiment variants
  // on the per-instance wrapper. Bind those containers and route analytics +
  // re-render through applyVariant. (No-op in the dev loop, which has no SDK.)
  const snippetApi = window.__spectrumAi?.snippet
  if (snippetApi && typeof snippetApi.bind === 'function') {
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )
    for (const node of containers) {
      const handles = snippetApi.bind(node, ({ pools, variants, currentVariantId }) => {
        const variant = variants.find((v) => v.variantId === currentVariantId)
        if (!variant || !variant.content) return
        applyVariant(node, variant.content, pools || {})
      })
      const root = node.querySelector(ROOT_SELECTOR)
      const instance = root ? instancesByRoot.get(root) : null
      if (instance && handles && typeof handles.track === 'function') {
        instance.setTrack(handles.track)
      }
    }
  }
})()
